import { css } from '@emotion/react';
import { BORDER_RADIUS, BORDER_WIDTH } from '../../styles/borders';
import { MEDIA_QUERY } from '../../styles/breakpoints';
import { COLOR, GREYSCALE } from '../../styles/colors';
import { SPACING } from '../../styles/spacing';
import { TYPOGRAPHY } from '../../styles/typography';
import { ButtonSize, ButtonVariant } from './Button';

/* Button sizes */
export const getButtonSizeStyle = (size: ButtonSize = 'medium') => {
  switch (size) {
    case 'small':
      return css`
        height: 28px;
        font-size: ${TYPOGRAPHY.fontSize.sm};
        font-weight: ${TYPOGRAPHY.fontWeight.regular};
        padding: 0 10px;
        border-width: ${BORDER_WIDTH.xs};
        border-radius: ${BORDER_RADIUS.xs};
      `;
    case 'medium':
      return css`
        height: 50px;
        font-size: ${TYPOGRAPHY.fontSize.md};
        font-weight: ${TYPOGRAPHY.fontWeight.regular};
        padding: 2px 9px ${SPACING.none};
        border-width: ${BORDER_WIDTH.sm};
        border-radius: ${BORDER_RADIUS.sm};
      `;
    case 'large':
      return css`
        height: 83px;
        font-size: ${TYPOGRAPHY.fontSize.xl};
        font-weight: ${TYPOGRAPHY.fontWeight.medium};
        padding: ${SPACING.none} 25px;
        border-width: ${BORDER_WIDTH.lg};
        border-radius: ${BORDER_RADIUS.lg};

        @media (min-width: ${MEDIA_QUERY.smMin}) {
          min-width: 218px;
        }
      `;
    case 'xLarge':
      return css`
        height: 100px;
        font-size: ${TYPOGRAPHY.fontSize.xxl};
        font-weight: ${TYPOGRAPHY.fontWeight.bold};
        padding: ${SPACING.none} 36px;
        border-width: ${BORDER_WIDTH.lg};
        border-radius: ${BORDER_RADIUS.lg};

        @media (min-width: ${MEDIA_QUERY.smMin}) {
          min-width: 320px;
        }
      `;
    default:
      // If the exhaustiveCheck line shows an error, the switch is missing a case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: string = size;

      // This should never happen
      throw new Error(`Unhandled size case ${exhaustiveCheck}`);
  }
};

/* Button variants */
export const getButtonVariantStyle = (variant: ButtonVariant = 'primary') => {
  switch (variant) {
    case 'primary':
    case 'info':
      return css`
        background-color: ${COLOR.blue};
        border-color: ${COLOR.blueButtonAccent};
        color: ${GREYSCALE.white};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.blueButtonAccent};
          :disabled {
            background-color: ${COLOR.blue};
          }
        }
      `;
    case 'primaryOutline':
      return css`
        background-color: transparent;
        border-color: ${COLOR.blueButtonAccent};
        color: ${COLOR.blueButtonAccent};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.blueButtonAccent};
          color: ${GREYSCALE.white};
          :disabled {
            background-color: transparent;
            color: ${COLOR.blueButtonAccent};
          }
        }
      `;
    case 'successOutline':
      return css`
        background-color: transparent;
        border-color: ${COLOR.greenButtonAccent};
        color: ${COLOR.greenButtonAccent};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.greenButtonAccent};
          color: ${GREYSCALE.white};
          :disabled {
            background-color: transparent;
            color: ${COLOR.greenButtonAccent};
          }
        }
      `;
    case 'success':
      return css`
        background-color: ${COLOR.green};
        border-color: ${COLOR.greenButtonAccent};
        color: ${GREYSCALE.white};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.greenButtonAccent};
          :disabled {
            background-color: ${COLOR.green};
          }
        }
      `;
    case 'warning':
      return css`
        background-color: ${COLOR.yellow};
        border-color: ${COLOR.yellowButtonAccent};
        color: ${GREYSCALE.white};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.yellowButtonAccent};
          :disabled {
            background-color: ${COLOR.yellow};
          }
        }
      `;
    case 'danger':
      return css`
        background-color: ${COLOR.red};
        border-color: ${COLOR.redButtonAccent};
        color: ${GREYSCALE.white};
        :hover,
        :active,
        :focus {
          background-color: ${COLOR.redButtonAccent};
          :disabled {
            background-color: ${COLOR.red};
          }
        }
      `;
    case 'dark':
      return css`
        background-color: ${GREYSCALE.grey80};
        border-color: ${GREYSCALE.black};
        color: ${GREYSCALE.grey10};
        :hover,
        :active,
        :focus {
          background-color: ${GREYSCALE.grey60};
          :disabled {
            background-color: ${GREYSCALE.grey80};
          }
        }
      `;
    default:
      // If the exhaustiveCheck line shows an error, the switch is missing a case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: string = variant;

      // This should never happen
      throw new Error(`Unhandled variant case ${exhaustiveCheck}`);
  }
};
